/**
 * Input Method Overlay
 * Main component that renders overlays for different input methods
 * - Virtual Cursor for gaze/head tracking
 * - Focus ring for scanning
 * - Number tags for voice control
 * - Scroll buttons for head tracking (only when page has scrollable content)
 * 
 * Note: All accessibility overlays are disabled on caregiver-only pages
 * (accessed via hamburger menu) - these pages use only mouse/touch control.
 */

import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useInputMethod } from '@/contexts/InputMethodContext';
import { InputMethod, INPUT_GROUPS } from '@/lib/inputMethods';
import VirtualCursor from './VirtualCursor';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';
import { isCaregiverRoute } from '@/lib/caregiverRoutes';

export default function InputMethodOverlay() {
  const {
    inputMethod,
    inputGroup,
    updateCursorPosition,
    setDwellProgress,
    navigateFocus,
    showVirtualCursor,
    isDwellEnabled
  } = useInputMethod();

  const location = useLocation();
  const { isRTL } = useTranslation();
  const [trackingActive, setTrackingActive] = useState(false);
  const [mouthGestureActive, setMouthGestureActive] = useState(false);
  const [hasScrollableContent, setHasScrollableContent] = useState(false);
  const [scrollContainer, setScrollContainer] = useState<Element | null>(null);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Check if current page is a caregiver-only page (hamburger menu destinations)
  // These pages disable all accessibility input methods
  const isCaregiverPage = isCaregiverRoute(location.pathname);

  // Only activate tracking when the specific mode is selected AND not on caregiver pages
  const isTrackingMode = !isCaregiverPage && (inputMethod === InputMethod.HEAD_TRACKING || inputMethod === InputMethod.GAZE_TRACKING);
  const isMouthGestures = !isCaregiverPage && inputMethod === InputMethod.MOUTH_GESTURES;
  
  // Hide virtual cursor on setup/calibration pages - these pages manage their own UI
  const isSetupPage = location.pathname.includes('/control/');
  
  // Always show scroll buttons on setup pages or when tracking mode is active
  // But not on caregiver pages
  const showScrollButtons = !isCaregiverPage && (isTrackingMode || isSetupPage) && hasScrollableContent;

  // Check if page has scrollable content - check both document and main content containers
  useEffect(() => {
    const checkScrollable = () => {
      // Check document-level scrolling
      const docHasScroll = document.documentElement.scrollHeight > window.innerHeight;
      
      // Check main content container (overflow-auto or overflow-y-auto)
      const mainContent = document.querySelector('main') || document.querySelector('.flex-1.overflow-auto') || document.querySelector('[class*="overflow-auto"]');
      
      let containerHasScroll = false;
      if (mainContent) {
        containerHasScroll = mainContent.scrollHeight > mainContent.clientHeight;
        if (containerHasScroll) {
          setScrollContainer(mainContent);
        }
      }
      
      setHasScrollableContent(docHasScroll || containerHasScroll);
    };
    
    // Small delay to let content render
    const timeoutId = setTimeout(checkScrollable, 100);
    
    checkScrollable();
    window.addEventListener('resize', checkScrollable);
    
    // Re-check on route change or DOM mutations
    const observer = new MutationObserver(() => {
      setTimeout(checkScrollable, 50);
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkScrollable);
      observer.disconnect();
    };
  }, [location.pathname]);

  const startScrolling = useCallback((direction: 'up' | 'down') => {
    if (scrollIntervalRef.current) return;
    
    const scrollAmount = direction === 'up' ? -40 : 40;
    scrollIntervalRef.current = setInterval(() => {
      if (scrollContainer) {
        scrollContainer.scrollBy({ top: scrollAmount, behavior: 'auto' });
      } else {
        window.scrollBy({ top: scrollAmount, behavior: 'auto' });
      }
    }, 50);
  }, [scrollContainer]);

  const stopScrolling = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
    };
  }, []);

  // Lazy load tracking engines only when needed
  useEffect(() => {
    if (!isTrackingMode) {
      setTrackingActive(false);
      return;
    }

    // Dynamically import and initialize MediaPipe tracking
    const initTracking = async () => {
      try {
        setTrackingActive(true);
      } catch {
        // Silently fail
      }
    };

    initTracking();

    return () => {
      setTrackingActive(false);
    };
  }, [isTrackingMode]);

  // Lazy load mouth gesture engine only when needed
  useEffect(() => {
    if (!isMouthGestures) {
      setMouthGestureActive(false);
      return;
    }

    setMouthGestureActive(true);

    return () => {
      setMouthGestureActive(false);
    };
  }, [isMouthGestures]);

  // Track whether a page-level scanning system is active
  const [pageHasGridScanning, setPageHasGridScanning] = useState(false);

  useEffect(() => {
    const handleMounted = () => setPageHasGridScanning(true);
    const handleUnmounted = () => setPageHasGridScanning(false);
    window.addEventListener('pageGridScanningMounted', handleMounted);
    window.addEventListener('pageGridScanningUnmounted', handleUnmounted);
    return () => {
      window.removeEventListener('pageGridScanningMounted', handleMounted);
      window.removeEventListener('pageGridScanningUnmounted', handleUnmounted);
    };
  }, []);

  // Keyboard navigation for scanning modes - disabled on caregiver pages
  useEffect(() => {
    // Disable scanning keyboard nav on caregiver pages
    if (isCaregiverPage) return;
    if (inputGroup !== INPUT_GROUPS.SCANNING) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          navigateFocus('up');
          break;
        case 'ArrowDown':
          e.preventDefault();
          navigateFocus('down');
          break;
        case 'ArrowLeft':
          e.preventDefault();
          navigateFocus(isRTL ? 'right' : 'left');
          break;
        case 'ArrowRight':
          e.preventDefault();
          navigateFocus(isRTL ? 'left' : 'right');
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          // When a page-level scanner is active, skip — it handles selection
          if (pageHasGridScanning) return;
          navigateFocus('enter');
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [inputGroup, navigateFocus, isCaregiverPage, pageHasGridScanning]);

  // All overlay elements have pointer-events: none to not block clicks
  // Using a wrapper div with inset-0 but pointer-events-none ensures nothing blocks the UI
  return (
    <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 9998 }}>
      {/* Virtual Cursor for gaze/head tracking - check inputMethod directly, hide on setup pages */}
      {isTrackingMode && !isSetupPage && <VirtualCursor />}
      
      {/* Floating Scroll Buttons for Head/Gaze Tracking - positioned at top/bottom edges */}
      {showScrollButtons && (
        <>
          <button
            onMouseEnter={() => startScrolling('up')}
            onMouseLeave={stopScrolling}
            onClick={() => {
              if (scrollContainer) {
                scrollContainer.scrollBy({ top: -200, behavior: 'smooth' });
              } else {
                window.scrollBy({ top: -200, behavior: 'smooth' });
              }
            }}
            className={cn(
              "fixed left-4 top-4 w-16 h-16 rounded-2xl flex items-center justify-center pointer-events-auto z-[9999]",
              "bg-card border-2 border-border shadow-xl",
              "hover:border-accent hover:ring-4 hover:ring-accent/30 hover:scale-110",
              "transition-all duration-200"
            )}
            style={{ backgroundColor: 'hsl(var(--card))' }}
          >
            <ChevronUp className="w-8 h-8 text-primary" />
          </button>
          <button
            onMouseEnter={() => startScrolling('down')}
            onMouseLeave={stopScrolling}
            onClick={() => {
              if (scrollContainer) {
                scrollContainer.scrollBy({ top: 200, behavior: 'smooth' });
              } else {
                window.scrollBy({ top: 200, behavior: 'smooth' });
              }
            }}
            className={cn(
              "fixed left-4 bottom-20 w-16 h-16 rounded-2xl flex items-center justify-center pointer-events-auto z-[9999]",
              "bg-card border-2 border-border shadow-xl",
              "hover:border-accent hover:ring-4 hover:ring-accent/30 hover:scale-110",
              "transition-all duration-200"
            )}
            style={{ backgroundColor: 'hsl(var(--card))' }}
          >
            <ChevronDown className="w-8 h-8 text-primary" />
          </button>
        </>
      )}
    </div>
  );
}
